/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { classes, Slot } from '@adobe-commerce/elsie/lib';
import { useGetStoreConfigs } from '@/auth/hooks/api/useGetStoreConfigs';
import { usePasswordValidationMessage } from '@/auth/hooks/components/usePasswordValidationMessage';
import { useUpdatePasswordForm } from '@/auth/hooks/components/useUpdatePasswordForm';
import { UpdatePasswordFormProps } from '@/auth/types';
import { Form, Button } from '@/auth/components';
import { useInLineAlert } from '@/auth/hooks/useInLineAlert';
import { Header, InLineAlert, InputPassword } from '@adobe-commerce/elsie/components';
import '@/auth/components/UpdatePasswordForm/UpdatePasswordForm.css';
import { useCustomTranslations } from '@/auth/hooks/useCustomTranslations';

export const UpdatePasswordForm: FunctionComponent<UpdatePasswordFormProps> = ({
  signInOnSuccess = true,
  formSize = 'default',
  routeRedirectOnSignIn,
  routeWrongUrlRedirect,
  routeSignInPage,
  slots,
  onErrorCallback,
  onSuccessCallback,
  routeRedirectOnPasswordUpdate,
}) => {
  /**
   * useCustomTranslations is required to support extensibility of error messages.
   * Ensure all error-related translation paths include ".default"
   * to allow future handling of dynamic or nested error messages.
   */
  const translations = useCustomTranslations({
    title: 'Auth.UpdatePasswordForm.title',
    buttonPrimary: 'Auth.UpdatePasswordForm.buttonPrimary',
    placeholder: 'Auth.InputPassword.placeholder',
    floatingLabel: 'Auth.InputPassword.floatingLabel',
    requiredFieldError: 'Auth.FormText.requiredFieldError.default',
  });

  const { passwordConfigs, isEmailConfirmationRequired } = useGetStoreConfigs();

  const { inLineAlertProps, handleSetInLineAlertProps } = useInLineAlert();

  const {
    additionalActionsAlert,
    passwordError,
    isSuccessful,
    updatePasswordValue,
    isClickSubmit,
    isLoading,
    submitUpdatePassword,
    handleSetUpdatePasswordValue,
  } = useUpdatePasswordForm({
    isEmailConfirmationRequired,
    signInOnSuccess,
    passwordConfigs,
    routeRedirectOnSignIn,
    routeWrongUrlRedirect,
    onErrorCallback,
    onSuccessCallback,
    handleSetInLineAlertProps,
    routeRedirectOnPasswordUpdate,
    routeSignInPage,
  });

  const { isValidUniqueSymbols, defaultLengthMessage } =
    usePasswordValidationMessage({
      password: updatePasswordValue,
      isClickSubmit,
      passwordConfigs,
    });

  if (isSuccessful.status && slots?.SuccessNotification) {
    return (
      <Slot
        data-testid="successNotificationTestId"
        name="SuccessNotification"
        slot={slots?.SuccessNotification}
        context={{ isSuccessful }}
      />
    );
  }

  const slotContext = {
    updatePasswordValue,
    isLoading,
    passwordError,
    isSuccessful,
  };

  return (
    <div
      className={classes([
        'auth-update-password-form',
        `auth-update-password-form--${formSize}`,
      ])}
    >
      <Slot
        name="Title"
        data-testid="title"
        slot={slots?.Title}
        context={{ ...slotContext }}
      >
        <Header
          title={translations.title}
          divider={false}
          className="auth-update-password-form__title"
        />
      </Slot>
      <InLineAlert
        className={classes([
          'auth-update-password-form__notification',
          [
            'auth-update-password-form__notification--show',
            !!inLineAlertProps?.text,
          ],
        ])}
        variant="secondary"
        heading={inLineAlertProps?.text as string}
        icon={inLineAlertProps.icon}
        additionalActions={additionalActionsAlert}
      />
      <Form
        name="updatePassword_form"
        className="auth-update-password-form__form"
        onSubmit={submitUpdatePassword}
        loading={isLoading}
        fieldsConfig={[]}
        formSlot={{
          name: 'Form',
          slot: slots?.Form,
          context: slotContext,
          dataTestId: 'form',
        }}
      >
        <div style="display: none;">
          <input
            type="text"
            id="username"
            name="username"
            autoComplete="username"
          />
        </div>
        <InputPassword
          defaultValue={updatePasswordValue}
          onValue={handleSetUpdatePasswordValue}
          className="auth-update-password-form__form__item"
          autoComplete={'new-password'}
          name={'password'}
          errorMessage={
            passwordError ||
              isValidUniqueSymbols === 'error' ||
              defaultLengthMessage?.status === 'error'
              ? translations.requiredFieldError
              : undefined
          }
          minLength={passwordConfigs?.minLength}
          uniqueSymbolsStatus={isValidUniqueSymbols}
          validateLengthConfig={defaultLengthMessage}
          requiredCharacterClasses={passwordConfigs?.requiredCharacterClasses}
          placeholder={translations.placeholder}
          floatingLabel={translations.floatingLabel}
        />

        <Slot
          name="Buttons"
          className="auth-update-password-form__button"
          data-testid="buttons"
          slot={slots?.Buttons}
          context={{ ...slotContext }}
        >
          <Button
            type="submit"
            buttonText={translations.buttonPrimary}
            variant="primary"
            enableLoader={isLoading}
          />
        </Slot>
      </Form>
    </div>
  );
};
